<template>
  <!-- Limit reached — show minimal content only -->
  <template v-if="limitReached">
    <ViewerContent />
  </template>

  <!-- Normal layout routing -->
  <component v-else :is="activeLayout" @addedComment="addedComment" />
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';

import ViewerContent from './Content.vue';

import VideoOwnerLayout from './layouts/owner/video/VideoOwnerLayout.vue';
import VideoGuestLayout from './layouts/guest/video/VideoGuestLayout.vue';
import ContentOwnerLayout from './layouts/owner/content/ContentOwnerLayout.vue';
import ContentGuestLayout from './layouts/guest/content/ContentGuestLayout.vue';

import { useViewerTrack, TrackEvents } from '@/modules/viewer/composables/useViewerTrack';
import { useViewerLayout } from '../composables/useViewerLayout';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useAuthModals } from '@/composables/useAuthModals';
import { useUserStore } from '@/store/useUserStore';
import { useFeatures } from '@/composables/useFeatures';
import { useSettingsStore } from '@/store/useSettingsStore';

const { shouldShowVideoOwnerLayout, shouldShowVideoGuestLayout, shouldShowContentOwnerLayout, shouldShowContentGuestLayout } = useViewerLayout();

const store = useViewerItemStore();
const { limitReached, hideBranding } = storeToRefs(store);
const { showSignUpModal } = useAuthModals();
const { getItemFeature } = useFeatures();
const { secondaryBaseDomain, isCustomDomain } = storeToRefs(useSettingsStore());

const { track } = useViewerTrack();
const userStore = useUserStore();
const showSignupModalBeta = getItemFeature('web-experiment-logged-out-signup', false);

const activeLayout = computed(() => {
  if (shouldShowVideoOwnerLayout.value) {
    return VideoOwnerLayout;
  }
  if (shouldShowVideoGuestLayout.value) {
    return VideoGuestLayout;
  }
  if (shouldShowContentOwnerLayout.value) {
    return ContentOwnerLayout;
  }
  return ContentGuestLayout;
});

onMounted(() => {
  if (!(secondaryBaseDomain?.value && origin.search(secondaryBaseDomain.value) > -1)) {
    if (!userStore.isLoggedIn && showSignupModalBeta.value && !hideBranding.value && !isCustomDomain?.value) {
      showSignUpModal({
        showModalForFreeTier: true,
        signedUpFrom: 'viewer_modal_logged_out',
      });
    }
  }
});

async function addedComment() {
  if (userStore.isLoggedIn || hideBranding.value) {
    return;
  }

  await track(TrackEvents.PROMPT_SIGN_UP_COMMENT);
  await showSignUpModal({ sidebarText: 'Join Zight to add your name to the conversation' });
}
</script>
